/* eslint-disable no-secrets/no-secrets */
import React, { useContext, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { CoverageComponent } from "premier-capability-policyjob-react";
import QuoteValidityComponent from 'premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent';
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import styles from './QuotePage.module.scss';
import presentationMetadata from './QuotePage.metadata.json5';
import messages from './QuotePage.messages';

function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;

    const breakpoint = useContext(BreakpointTrackerContext);
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const handleNext = useCallback(
        () => {
            const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

            _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

            return submissionVM;
        },
        [submissionVM]
    );

    const navigateToCoverages = useCallback(
        () => {
            const indexOfGeneralCoverages = _.findIndex(
                steps,
                ({ path }) => path === '/liabilityCoveragesPage'
            );

            jumpTo(indexOfGeneralCoverages);
        },
        [jumpTo, steps]
    );

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getFormTableData = () => _.map(submissionVM.value.lobData.spaAndWellLine.lineCoverages.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const getInternalUseOnlyConditionsOfQuoteTableData = () => {
        const internalUseOnlySubConData = _.get(submissionVM.value, 'lobData.spaAndWellLine.coverables.internalUseOnly.pswsubConOfQuoExps')

        return _.map(internalUseOnlySubConData, (plesubofQuoExp) => ({
            conFieConditionsOfQuote: plesubofQuoExp
        }))
    };

    const getInternalUseOnlyAdditionalConditionsTableData = () => {
        const internalUseOnlyAddConData = _.get(submissionVM.value, `lobData.spaAndWellLine.coverables.internalUseOnly.pswaddConofPolExps`)

        return _.map(internalUseOnlyAddConData, (pleaddConofPolExp) => ({
            conFieAdditionalConditions: pleaddConofPolExp
        }))
    }

    const checkInternalUseVisibility = () => {
        const internalUseOnlySubConData = _.get(submissionVM.value, `lobData.spaAndWellLine.coverables.internalUseOnly.pswsubConOfQuoExps`)
        const internalUseOnlyAddConData = _.get(submissionVM.value, `lobData.spaAndWellLine.coverables.internalUseOnly.pswaddConofPolExps`)

        if (internalUseOnlySubConData.length > 0 || internalUseOnlyAddConData.length > 0) {
            return true
        }

        return false

    };

    const checkInternalUseSubConVisibility = () => {
        const internalUseOnlySubConData = _.get(submissionVM.value, `lobData.spaAndWellLine.coverables.internalUseOnly.pswsubConOfQuoExps`)

        if (!_.isUndefined(internalUseOnlySubConData) && internalUseOnlySubConData.length > 0) {
            return true
        }

        return false

    };

    const checkInternalUseAddConVisibility = () => {
        const internalUseOnlyAddConData = _.get(submissionVM.value, `lobData.spaAndWellLine.coverables.internalUseOnly.pswaddConofPolExps`)

        if (!_.isUndefined(internalUseOnlyAddConData) && internalUseOnlyAddConData.length > 0) {
            return true
        }

        return false

    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString,
        },
        resolveComponentMap: {
            coveragespagecomponent: CoverageComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.spaAndWellLine.lineCoverages.coverages`,
            title: messages.covAccordion,
            clasueType: 'coverages',
            isEditable: false,
            pathToGet: 'spaAndWellLine.coverages'
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        policyQuoteValDaysHead: {
            content: translator(messages.plQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyAccordion: {
            visible: checkInternalUseVisibility()
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: checkInternalUseSubConVisibility()
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: checkInternalUseAddConVisibility()
        },
        liabilityAccordion: {
            visible: _.get(submissionVM, `lobData.spaAndWellLine.lineCoverages.coverages`).length > 0
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.spaAndWellLine.lineCoverages.forms_Ext
        },
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
